import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import {
  Eye, Camera, Wifi, AlertTriangle, CheckCircle, Shield,
  ChevronDown, ChevronUp, Zap, Users, Radio, MapPin,
  Clock, Battery, Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useTranslate } from "../i18n/useTranslate";

// ── inline data ──────────────────────────────────────────────────
const CCTV_ZONES = [
  { zone:"Ramkund Ghat",    cams:64, online:62, offline:2, aiActive:60, resolution:"1080p", nightVision:true,  alerts:3, coverage:"92%" },
  { zone:"Panchvati",       cams:48, online:48, offline:0, aiActive:46, resolution:"1080p", nightVision:true,  alerts:1, coverage:"96%" },
  { zone:"Trimbakeshwar",   cams:56, online:54, offline:2, aiActive:52, resolution:"4K",    nightVision:true,  alerts:0, coverage:"89%" },
  { zone:"Nashik Road",     cams:40, online:40, offline:0, aiActive:38, resolution:"1080p", nightVision:true,  alerts:1, coverage:"94%" },
  { zone:"Gangapur Camp",   cams:32, online:31, offline:1, aiActive:30, resolution:"1080p", nightVision:true,  alerts:0, coverage:"88%" },
  { zone:"Kalaram Temple",  cams:28, online:28, offline:0, aiActive:26, resolution:"1080p", nightVision:false, alerts:0, coverage:"91%" },
  { zone:"Traffic Junctions",cams:22,online:22, offline:0, aiActive:22, resolution:"4K",    nightVision:true,  alerts:2, coverage:"100%"},
];

const DRONE_FLEET = [
  { id:"UAV-01", model:"DJI Matrice 350",   zone:"Ramkund Ghat",     battery:78, altitude:"80m", status:"Aerial Patrol", operator:"UNIT-P18",    range:"2km",  camera:"4K thermal" },
  { id:"UAV-02", model:"DJI Matrice 350",   zone:"Panchvati",        battery:62, altitude:"70m", status:"Crowd Monitor", operator:"UNIT-P21",          range:"2km",  camera:"4K thermal" },
  { id:"UAV-03", model:"Garuda Aerospace",  zone:"Trimbakeshwar",    battery:88, altitude:"60m", status:"Aerial Patrol", operator:"UNIT-P14",  range:"5km",  camera:"2K zoom"    },
  { id:"UAV-04", model:"DJI Matrice 350",   zone:"Nashik Road",      battery:44, altitude:"90m", status:"Traffic Watch", operator:"UNIT-P19",        range:"2km",  camera:"4K thermal" },
  { id:"UAV-05", model:"Garuda Aerospace",  zone:"EOC Base",         battery:100,altitude:"0m",  status:"Charging",      operator:"UNIT-P12",      range:"5km",  camera:"2K zoom"    },
  { id:"UAV-06", model:"DJI Mini 4 Pro",    zone:"Gangapur Camp",    battery:54, altitude:"50m", status:"Area Survey",   operator:"UNIT-P15",       range:"1.5km",camera:"4K" },
];

const BODY_CAMS = [
  { officer:"UNIT-P22",   zone:"Ramkund",        battery:72, recording:true,  storage:"8.4 GB used",  lastSync:"14:30", status:"Active" },
  { officer:"UNIT-P26", zone:"Panchvati",      battery:88, recording:true,  storage:"6.2 GB used",  lastSync:"14:28", status:"Active" },
  { officer:"UNIT-P14",    zone:"Trimbak",        battery:54, recording:true,  storage:"4.8 GB used",  lastSync:"14:25", status:"Active" },
  { officer:"UNIT-P28",     zone:"Gangapur Camp",  battery:34, recording:false, storage:"3.1 GB used",  lastSync:"13:45", status:"Low Bat"},
  { officer:"UNIT-P16",    zone:"Panchvati S-7",  battery:91, recording:true,  storage:"11.2 GB used", lastSync:"14:33", status:"Active" },
  { officer:"UNIT-P20",     zone:"Kalaram Temple", battery:78, recording:true,  storage:"5.6 GB used",  lastSync:"14:20", status:"Active" },
];

const QRT_TEAMS = [
  { team:"QRT Alpha — Ramkund",    strength:12, vehicle:"PCR Van + Bike",   armed:true,  eta:"<3 min", status:"Standby",  zone:"Ramkund Zone A" },
  { team:"QRT Bravo — Panchvati",  strength:10, vehicle:"PCR Van",          armed:true,  eta:"<4 min", status:"Active",   zone:"Panchvati" },
  { team:"QRT Charlie — Trimbak",  strength:12, vehicle:"PCR Van + Jeep",   armed:true,  eta:"<5 min", status:"Standby",  zone:"Trimbakeshwar" },
  { team:"QRT Delta — Nashik Rd",  strength:8,  vehicle:"Bike Squad (4)",   armed:false, eta:"<3 min", status:"Standby",  zone:"Nashik Road" },
  { team:"QRT Echo — Mobile",      strength:10, vehicle:"Bus + PCR Van",    armed:true,  eta:"<6 min", status:"Transit",  zone:"Gangapur→Panchvati" },
];

const PATROL_COVERAGE = [
  { zone:"Ramkund Ghat",   coverage:96, officers:284, sectors:6, lastPatrol:"14:30" },
  { zone:"Panchvati",      coverage:92, officers:212, sectors:5, lastPatrol:"14:28" },
  { zone:"Trimbakeshwar",  coverage:88, officers:188, sectors:6, lastPatrol:"14:22" },
  { zone:"Nashik Road",    coverage:84, officers:142, sectors:4, lastPatrol:"14:18" },
  { zone:"Gangapur Camp",  coverage:78, officers:98,  sectors:3, lastPatrol:"14:10" },
  { zone:"Kalaram Temple", coverage:90, officers:124, sectors:4, lastPatrol:"14:25" },
];

const FIR_TREND = [
  { hour:"06:00", fir:2, cognizable:1, nonCog:1, closed:1 },
  { hour:"08:00", fir:5, cognizable:3, nonCog:2, closed:4 },
  { hour:"10:00", fir:8, cognizable:5, nonCog:3, closed:6 },
  { hour:"12:00", fir:6, cognizable:4, nonCog:2, closed:5 },
  { hour:"14:00", fir:4, cognizable:2, nonCog:2, closed:3 },
];

const AI_EVENTS = [
  { id:"AI-228", event:"Crowd density alert — Ramkund Entry 2",  cam:"CCTV-RK-012", conf:91, action:"Sent to DCP",      time:"14:31", status:"Actioned"  },
  { id:"AI-227", event:"Unattended bag — Panchvati Zone 3",      cam:"CCTV-PV-008", conf:78, action:"UNIT-P14",     time:"14:24", status:"Verified"  },
  { id:"AI-226", event:"Face match — Missing child MP-1185",     cam:"CCTV-PV-Gate5",conf:61, action:"UNIT-P16",   time:"14:12", status:"Low-conf"  },
  { id:"AI-225", event:"Aggressive crowd behavior — Zone A5",    cam:"CCTV-RK-028", conf:84, action:"QRT Bravo",       time:"14:08", status:"Resolved"  },
  { id:"AI-224", event:"Vehicle obstruction — Trimbak Road",     cam:"CCTV-TK-003", conf:96, action:"Traffic officer", time:"14:02", status:"Cleared"   },
  { id:"AI-223", event:"Perimeter breach — Camp B restricted",   cam:"CCTV-GG-011", conf:88, action:"UNIT-P28",    time:"13:55", status:"Resolved"   },
];

const DIGITAL_FIR = [
  { fir:"FIR-2027-442", type:"Cognizable", crime:"Theft at Camp Alpha",       ps:"Nashik City PS",  status:"Under Inv.", complainant:"S. Kulkarni"  },
  { fir:"FIR-2027-441", type:"Non-Cog",    crime:"Minor dispute — Ghat area", ps:"Panchvati PS",    status:"Resolved",   complainant:"R. Sharma"    },
  { fir:"FIR-2027-440", type:"Cognizable", crime:"Pickpocketing — Entry 2",   ps:"Nashik City PS",  status:"Arrested",   complainant:"T. Jadhav"    },
  { fir:"FIR-2027-439", type:"Non-Cog",    crime:"Missing person — MP-1185",  ps:"Panchvati PS",    status:"Active",     complainant:"M. Patil"     },
  { fir:"FIR-2027-438", type:"Cognizable", crime:"Impersonation — volunteer", ps:"Trimbak PS",      status:"Arrested",   complainant:"Maha Kumbh AI System"  },
];

const AI_RADAR = [
  { metric:"CCTV Coverage",    value:92 },
  { metric:"Drone Uptime",     value:83 },
  { metric:"Body Cam Active",  value:88 },
  { metric:"Patrol Coverage",  value:88 },
  { metric:"Digital FIR",      value:96 },
  { metric:"QRT Readiness",    value:94 },
];

const TECH_PIE = [
  { name:"Cameras Online",   value:285, color:"#2563EB" },
  { name:"Cameras Offline",  value:5,   color:"#EF4444" },
];

// ── helpers ──────────────────────────────────────────────────────
// Battery % -> shared STATUS bucket (higher is better).
const BATTERY_BANDS = [{ min:50, bucket:"good" }, { min:30, bucket:"warning" }, { min:0, bucket:"critical" }];
const batColor = b => STATUS[thresholdStatus(b, BATTERY_BANDS)].text;

const stBadge = s =>
  s === "Active"   ? "badge badge-success" :
  s === "Standby"  ? "badge badge-info"    :
  s === "Transit"  ? "badge badge-warning" :
  s === "Low Bat"  ? "badge badge-critical": "badge badge-navy";

// AI surveillance event status -> shared STATUS bucket (exact class match to
// the old local per-status hex chain).
const aiEventBucket = s =>
  s === "Actioned" ? "info"    :
  s === "Resolved" ? "good"    :
  s === "Verified" ? "warning" :
  s === "Cleared"  ? "neutral" : "critical";

// Digital FIR type/status -> shared STATUS bucket.
const firTypeBucket = t => t === "Cognizable" ? "critical" : "neutral";
const firStatusBucket = s =>
  s === "Arrested"   ? "critical" :
  s === "Resolved"   ? "good"     :
  s === "Under Inv." ? "info"     : "warning";

// ── component ────────────────────────────────────────────────────
export default function PoliceModernization() {
  const [expandedDrone, setExpandedDrone] = useState(null);
  const t = useTranslate();

  // Computed from CCTV_ZONES so the header badge can never drift from the
  // zone table beneath it (it previously hardcoded "290 CAMS LIVE" even
  // though 5 of those 290 are offline per the same table).
  const camsTotal  = CCTV_ZONES.reduce((a, z) => a + z.cams, 0);
  const camsOnline = CCTV_ZONES.reduce((a, z) => a + z.online, 0);
  const aiEventsToday = 228; // AI-228 is today's latest sequential event ID; AI_EVENTS below shows only the most recent

  return (
    <div className="space-y-8">
      <SectionHeader icon="Cctv" title="Police Modernization Hub"
        subtitle="Smart CCTV surveillance · UAV drone fleet · body cameras · digital FIR management · QRT rapid response · AI-powered crowd analytics · patrol coverage · technology modernization — Maha AI · Simhastha 2027"
        badge={`${camsOnline} / ${camsTotal} ${t("LIVE")}`}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="CCTV Online"      value="285 / 290" icon="Camera"       color="blue"   index={0} live />
        <MetricCard title="Drones Active"    value="5 / 6"     icon="Zap"          color="green"  index={1} live />
        <MetricCard title="Body Cams"        value="5 / 6"     icon="Video"        color="blue"   index={2} />
        <MetricCard title="QRT Teams"        value="5 Online"  icon="Shield"       color="green"  index={3} />
        <MetricCard title="FIR Today"        value="5"         icon="FileText"     color="amber"  index={4} />
        <MetricCard title="AI Events"        value="228"       icon="Brain"        color="purple" index={5} />
        <MetricCard title="Patrol Coverage"  value="88%"       icon="MapPin"       color="green"  index={6} />
        <MetricCard title="Arrests Today"    value="2"         icon="Lock"         color="red"    index={7} />
      </div>

      {/* Police Modernization Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-blue-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("MAHA AI — POLICE MODERNIZATION HUB · SP NASHIK · 6,420 DEPLOYED")} — 14:35 IST
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Smart Policing — Simhastha Kumbh 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("290 CCTV cameras (285 live) · 6 drones (5 active) · 6 body cams · 5 QRT teams · 228 AI events today · 88% patrol coverage · 2 arrests · 5 digital FIRs")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {AI_RADAR.map((r, i) => {
              const c = googleColor(i);
              return (
                <div key={r.metric}
                  className="rounded-xl px-2.5 py-2 text-center min-w-[76px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-[8px] mb-0.5 font-black uppercase tracking-wide" style={{ color:c.text }}>{t(r.metric)}</div>
                  <div className="text-sm font-black" style={{ color:c.text }}>{r.value}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* CCTV Zone Status */}
      <ChartCard title="CCTV Surveillance Network — Zone Status"
        subtitle="290 cameras · 7 zones · AI-active cameras · alerts per zone" live>
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-center">{t("Total")}</th>
                <th className="text-center text-emerald-700">{t("Online")}</th>
                <th className="text-center text-red-700">{t("Offline")}</th>
                <th className="text-center text-violet-700">{t("AI Active")}</th>
                <th className="text-center">{t("Resolution")}</th>
                <th className="text-center">{t("Coverage")}</th>
                <th className="text-center">{t("Alerts")}</th>
              </tr>
            </thead>
            <tbody>
              {CCTV_ZONES.map((z, i) => (
                <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-black text-slate-900">
                    <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline">{t(z.zone)}</Link>
                  </td>
                  <td className="text-center font-semibold">{z.cams}</td>
                  <td className="text-center font-black text-emerald-600">{z.online}</td>
                  <td className="text-center font-black">{z.offline > 0 ? <span className="text-red-600">{z.offline}</span> : <span className="text-white/60">—</span>}</td>
                  <td className="text-center font-black text-violet-600">{z.aiActive}</td>
                  <td className="text-center">
                    <span className="text-[9px] font-bold text-blue-700">{z.resolution}</span>
                  </td>
                  <td className="text-center font-black text-emerald-600">{z.coverage}</td>
                  <td className="text-center">
                    {z.alerts > 0
                      ? <span className="text-[9px] font-black text-red-600">⚠ {z.alerts}</span>
                      : <CheckCircle size={10} className="text-emerald-500 mx-auto" />}
                  </td>
                </motion.tr>
              ))}
              <tr className="bg-slate-50 font-black text-slate-700">
                <td>{t("Total")}</td>
                <td className="text-center">{CCTV_ZONES.reduce((a,z)=>a+z.cams,0)}</td>
                <td className="text-center text-emerald-600">{CCTV_ZONES.reduce((a,z)=>a+z.online,0)}</td>
                <td className="text-center text-red-600">{CCTV_ZONES.reduce((a,z)=>a+z.offline,0)}</td>
                <td className="text-center text-violet-600">{CCTV_ZONES.reduce((a,z)=>a+z.aiActive,0)}</td>
                <td colSpan={2} className="text-center text-slate-400 font-normal">—</td>
                <td className="text-center text-red-600">{CCTV_ZONES.reduce((a,z)=>a+z.alerts,0)} {t("total")}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-violet-600 font-bold mt-2">
          ⚠ {t("AI face recognition — DPDP 2023 compliant · No punitive action without human officer approval · Crowd analytics only at aggregate level")}
        </p>
      </ChartCard>

      {/* Drone Fleet + Body Cams */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="UAV Drone Fleet — Live Status"
          subtitle="6 drones · zone · battery · task · operator">
          <div className="space-y-2">
            {DRONE_FLEET.map((d, i) => (
              <motion.div key={d.id}
                initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                className={`p-3 rounded-xl border ${
                  d.battery < 30 ? "border-red-200 bg-red-50/10"    :
                  d.status === "Charging" ? "border-slate-200 bg-slate-50/50" : "border-blue-200 bg-blue-50/10"
                }`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="font-mono text-[8px] text-slate-400">{d.id}</span>
                      <span className="text-[9px] font-black text-blue-700">{d.model}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                        d.status === "Charging" ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-blue-700"
                      }`}>{t(d.status)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500 flex-wrap">
                      <MapPin size={8} />{t(d.zone)}
                      <span>· {t("Alt")}: {d.altitude}</span>
                      <span>· {t(d.camera)}</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">{d.operator}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-xl font-black ${batColor(d.battery)}`}>{d.battery}%</div>
                    <div className="text-[9px] text-slate-500">{t("battery")}</div>
                    <div className="text-[9px] text-slate-400">{d.range} {t("range")}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Body Camera Status — Key Officers"
          subtitle="6 body cams · recording status · battery · storage · last sync">
          <div className="space-y-2">
            {BODY_CAMS.map((b, i) => (
              <motion.div key={b.officer}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className={`p-3 rounded-xl border ${b.battery < 40 ? `${STATUS.warning.border} ${STATUS.warning.bg}/20` : "border-slate-200"}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[140px]">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-xs font-black text-slate-900">{b.officer}</span>
                      <span className={stBadge(b.status)}>{t(b.status)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500 flex-wrap">
                      <MapPin size={8} />{t(b.zone)}
                      <span>·</span>
                      <span className={`font-bold ${b.recording ? "text-red-600" : "text-slate-400"}`}>
                        {b.recording ? `● ${t("REC")}` : `○ ${t("Off")}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-400">
                      <span>{b.storage}</span>
                      <span>· {t("Sync")}: {b.lastSync}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-xl font-black ${batColor(b.battery)}`}>{b.battery}%</div>
                    <div className="text-[9px] text-slate-500">{t("battery")}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-[9px] text-violet-600 font-bold mt-2 pt-2 border-t border-slate-100">
            ⚠ {t("Body cam footage stored encrypted for 30 days · DPDP 2023 compliant · Access requires DCP authorisation")}
          </p>
        </ChartCard>
      </div>

      {/* QRT + Patrol */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Quick Response Team (QRT) Board"
          subtitle="5 QRT teams · strength · vehicle · ETA · zone">
          <div className="space-y-2">
            {QRT_TEAMS.map((q, i) => (
              <motion.div key={q.team}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${q.status === "Active" ? "border-amber-200 bg-amber-50/20" : "border-slate-200"}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-8 h-8 rounded-lg gov-gradient flex items-center justify-center flex-shrink-0">
                    <Shield size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-xs font-black text-slate-900">{t(q.team)}</span>
                      <span className={stBadge(q.status)}>{t(q.status)}</span>
                      {q.armed && <span className="text-[8px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">{t("Armed")}</span>}
                    </div>
                    <div className="text-[9px] text-slate-500">{t(q.vehicle)} · {q.strength} {t("officers")}</div>
                    <div className="text-[9px] text-slate-400 flex items-center gap-1"><MapPin size={8} />{t(q.zone)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-black text-emerald-600">{q.eta}</div>
                    <div className="text-[9px] text-slate-500">{t("response")}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Zone Patrol Coverage"
          subtitle="Officer deployment vs sector coverage across all 6 zones">
          <div className="space-y-2.5">
            {PATROL_COVERAGE.map((p, i) => (
              <motion.div key={p.zone}
                initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}>
                <div className="flex items-center justify-between mb-1 text-xs">
                  <Link to="/crowd-intelligence" className="font-bold text-slate-800 hover:text-blue-700 hover:underline">{t(p.zone)}</Link>
                  <div className="flex items-center gap-2 text-[9px] text-slate-500">
                    <Users size={9} />{p.officers} {t("officers")}
                    <span>· {p.sectors} {t("sectors")}</span>
                    <Clock size={9} />{p.lastPatrol}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div className={`h-3 rounded-full flex items-center justify-end pr-1.5 ${
                      p.coverage >= 90 ? "bg-emerald-500" : p.coverage >= 80 ? "bg-amber-500" : "bg-red-500"
                    }`} initial={{ width:0 }} animate={{ width:`${p.coverage}%` }} transition={{ duration:0.8 }}>
                      <span className="text-[8px] font-black text-white">{p.coverage}%</span>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* AI Events + FIR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="AI Surveillance Events — Recent" subtitle="AI-flagged events · confidence · assigned officer · status" live>
          <p className="text-[9px] text-slate-400 -mt-1 mb-2">
            {t("Showing")} {AI_EVENTS.length} {t("most recent of")} {aiEventsToday} {t("AI events today")}
          </p>
          <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
            {AI_EVENTS.map((e, i) => (
              <motion.div key={e.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-2 p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <span className="font-mono text-[8px] text-slate-400 w-10 flex-shrink-0 mt-0.5">{e.time}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <span className="font-mono text-[8px] text-slate-400">{e.id}</span>
                    <span className="text-[9px] text-violet-700 font-bold">{t("AI")} {e.conf}%</span>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${STATUS[aiEventBucket(e.status)].bg} ${STATUS[aiEventBucket(e.status)].text}`}>{t(e.status)}</span>
                  </div>
                  <p className="text-[10px] text-slate-700">{t(e.event)}</p>
                  <p className="text-[9px] text-slate-400">{e.cam} → {t(e.action)}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-[9px] text-violet-600 font-bold mt-2 pt-2 border-t border-slate-100">
            ⚠ {t("All AI flags require human officer verification before action — DPDP 2023 · Human-in-loop mandatory")}
          </p>
        </ChartCard>

        <ChartCard title="Digital FIR Management — Today's Cases"
          subtitle="All FIRs filed digitally — type · crime · status">
          <div className="space-y-2 mb-4">
            {DIGITAL_FIR.map((f, i) => (
              <motion.div key={f.fir}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className="p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="font-mono text-[9px] text-blue-700 font-bold">{f.fir}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${STATUS[firTypeBucket(f.type)].bg} ${STATUS[firTypeBucket(f.type)].text}`}>{t(f.type)}</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800">{t(f.crime)}</p>
                    <div className="text-[9px] text-slate-500 mt-0.5">{f.ps} · {t("Complainant")}: {f.complainant}</div>
                  </div>
                  <span className={`text-[8px] font-black px-2 py-1 rounded-full flex-shrink-0 ${STATUS[firStatusBucket(f.status)].bg} ${STATUS[firStatusBucket(f.status)].text}`}>{t(f.status)}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <ChartCard title="FIR Volume Trend — Today" subtitle="Hourly FIR filing vs closures">
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={FIR_TREND} margin={{ top:5, right:5, left:-20, bottom:0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="hour" tick={{ ...axisTick, fontSize:8 }} />
                <YAxis tick={{ ...axisTick, fontSize:8 }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="fir"    fill={chartColor(0)} radius={[2,2,0,0]} name={t("Filed")} />
                <Bar dataKey="closed" fill={chartColor(2)} radius={[2,2,0,0]} name={t("Closed")} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartCard>
      </div>
    </div>
  );
}
